/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */
//@ts-nocheck
import React from 'react';
import { Card, Dropdown, Button, Image, ListGroup } from 'react-bootstrap';
import {
  PencilIcon,
  PencilAltIcon,
  ClipboardCopyIcon,
  SelectorIcon,
  TrashIcon,
} from '@heroicons/react/solid';

export interface IKanbanCardProps {
  onEdit?(): void;
  onCopy?(): void;
  onMove?(): void;
  onDelete?(): void;
  onClick?(): void;
  cardRef: any;
  title: string;
  attributeType: string;
  isRequired?: string;
  attributeOptions?: string;
  image?: string;
  style?: any;
  extraProps?: any;
  editCardLabel?: string;
  copyCardLabel?: string;
  moveCardLabel?: string;
  removeCardLabel?: string;
}

export const KanbanCard = (props: IKanbanCardProps) => {
  const {
    cardRef,
    title,
    attributeType,
    attributeOptions,
    isRequired,
    image,
    style = {},
    extraProps = {},
  } = props;
  const onEdit = () => {
    props.onEdit && props.onEdit();
  };

  const onCopy = () => {
    props.onCopy && props.onCopy();
  };

  const onMove = () => {
    props.onMove && props.onMove();
  };

  const onDelete = () => {
    props.onDelete && props.onDelete();
  };

  const onCardClick = (e: any) => {
    if (e.defaultPrevented) return;

    props.onClick && props.onClick();
  };

  const onDropdownClick = (e: any) => {
    e.preventDefault();
  };

  const _renderDropDownActions = () => {
    return (
      <div>
        <Dropdown onClick={onDropdownClick}>
          <Dropdown.Toggle
            as={Button}
            variant="link"
            size="sm"
            className="fs-6 px-1 py-0"
          >
            <PencilAltIcon className="icon icon-xs text-gray-500" />
          </Dropdown.Toggle>

          <Dropdown.Menu className="dashboard-dropdown dropdown-menu-start mt-2 py-1">
            <Dropdown.Item
              className="d-flex align-items-center"
              onClick={onEdit}
            >
              <PencilIcon className="icon icon-xs text-gray-400 me-2" />{' '}
              {props.editCardLabel}
            </Dropdown.Item>
            <Dropdown.Item
              className="d-flex align-items-center"
              onClick={onCopy}
            >
              <ClipboardCopyIcon className="icon icon-xs text-gray-400 me-2" />{' '}
              {props.copyCardLabel}
            </Dropdown.Item>
            <Dropdown.Item
              className="d-flex align-items-center"
              onClick={onMove}
            >
              <SelectorIcon className="icon icon-xs text-gray-400 me-2" />{' '}
              {props.moveCardLabel}
            </Dropdown.Item>
            <Dropdown.Divider as="div" className="my-1" />

            <Dropdown.Item
              className="d-flex align-items-center"
              onClick={onDelete}
            >
              <TrashIcon className="icon icon-xs text-danger me-2" />{' '}
              {props.removeCardLabel}
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </div>
    );
  };

  const _renderBody = () => {
    return (
      <Card.Body className="p-0">
        {image && <Image src={image} className="card-img-top mb-2 mb-lg-3" />}
        <ListGroup variant="flush">
          <ListGroup.Item
            as="li"
            className="d-flex justify-content-between align-items-start"
          >
            <div className="ms-2 me-auto">
              <div className="fw-bold">Type</div>
              {attributeType}
            </div>
          </ListGroup.Item>
        </ListGroup>
        {attributeOptions && (
          <ListGroup variant="flush">
            <ListGroup.Item
              as="li"
              className="d-flex justify-content-between align-items-start"
            >
              <div className="ms-2 me-auto">
                <div className="fw-bold">Options</div>
                {attributeOptions}
              </div>
            </ListGroup.Item>
          </ListGroup>
        )}
        <ListGroup variant="flush">
          <ListGroup.Item
            as="li"
            className="d-flex justify-content-between align-items-start"
          >
            <div className="ms-2 me-auto">
              <div className="fw-bold">isRequired</div>
              {isRequired ? 'Yes' : 'NO'}
            </div>
          </ListGroup.Item>
        </ListGroup>
      </Card.Body>
    );
  };

  return (
    <Card
      border={1}
      className="p-4"
      ref={cardRef}
      {...extraProps}
      style={style}
      onClick={onCardClick}
    >
      <Card.Header className="d-flex align-items-center justify-content-between border-0 p-0 mb-3">
        <h5 className="mb-0">{title}</h5>
        {_renderDropDownActions()}
      </Card.Header>
      {_renderBody()}
    </Card>
  );
};

KanbanCard.defaultProps = {
  editCardLabel: 'Edit',
  copyCardLabel: 'Copy',
  moveCardLabel: 'Move',
  removeCardLabel: 'Remove',
};
